import { canAddTranscriber, isTranscribing } from '../transcribing/functions';
import LIBRE_SUPPORTED_LANGS from '../transcribing/libre-supported-langs.json';

/**
 * Every language captions can be translated into, as LibreTranslate codes, sorted.
 *
 * The union of every source's targets rather than the targets of one source: the spoken language is recognized
 * per sentence, so there is no single conference language to list targets for. A sentence whose own language
 * cannot reach the chosen target is shown in its original language and script instead (Jigasi checks the pair).
 */
export const TRANSLATION_TARGET_LANGUAGES = [ ...new Set(LIBRE_SUPPORTED_LANGS.flatMap(lang => lang.targets)) ].sort();

/**
 * The translation language to use when the listener has not picked one: their app language when captions can be
 * translated into it, otherwise English.
 *
 * @param {string|undefined} appLanguage - The app (UI) language, e.g. `hi`, `enGB`, `zhTW`.
 * @returns {{ language: string, fallback: boolean }} `fallback` is true when the app language could not be used.
 */
export function getDefaultTranslationLanguage(appLanguage) {
    // App locales spell Traditional Chinese as a region (zhTW), LibreTranslate as its own code (zt).
    const code = /^zh[-_]?(TW|HK|Hant)/i.test(appLanguage ?? '') ? 'zt' : getPrimaryLanguageCode(appLanguage);

    return TRANSLATION_TARGET_LANGUAGES.includes(code)
        ? { language: code,
            fallback: false }
        : { language: 'en',
            fallback: true };
}

/**
 * Checks whether the participant can start the subtitles.
 *
 * @param {Object} state - The redux state.
 * @returns {boolean} - True if the participant can start the subtitles.
 */
export function canStartSubtitles(state) {
    return canAddTranscriber(state) || isTranscribing(state);
}

/**
 * The primary (language-only) part of a language tag, so `en-GB`, `en_GB` and `enGB` all reduce to `en`.
 *
 * Used wherever a tag has to be matched against, or displayed from, a plain language code: the `languages:`
 * translation keys and `libre-supported-langs.json` are both keyed that way, and a tag that still carries its
 * region matches nothing - which is how a UI locale of `enIN` ended up rendering as the raw code and emptying
 * the translation target list.
 *
 * Stops at the first separator rather than taking two characters, so three-letter codes such as `fil` survive.
 *
 * @param {string|undefined} language - A language tag.
 * @returns {string|undefined}
 */
export function getPrimaryLanguageCode(language) {
    if (!language) {
        return undefined;
    }

    // `_language` is sometimes stored with this prefix, and its colon-and-dash would otherwise be mistaken for a
    // region separator - reducing `translation-languages:hi` to `translation`.
    return language.replace('translation-languages:', '').replace(/[-_A-Z].*/, '');
}
